
import React, { useEffect, useRef } from 'react';
import { Loader2Icon, CameraIcon, CameraOffIcon } from 'lucide-react';
import { MdMic, MdMicOff } from 'react-icons/md';

import './RemoteVideo.scss';

const RemoteVideo = ({
  stream,
  userId,
  isVideoOn,
  isAudioOn,
  isScreenshareOn,
  userName,
  className = '',
  isPip = false,
  userData,
  isDisconnected = false,
  onRemoveDisconnected,
}) => {
  const containerRef = useRef(null);
  const attachedVideoRef = useRef(null);
  const disconnectTimerRef = useRef(null);
  const [timeRemaining, setTimeRemaining] = React.useState(120);

  useEffect(() => {
    if (isDisconnected) {
      disconnectTimerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(disconnectTimerRef.current);
            disconnectTimerRef.current = null;
            if (onRemoveDisconnected) {
              onRemoveDisconnected(userId);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (disconnectTimerRef.current) {
        clearInterval(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      setTimeRemaining(120);
    }
    return () => {
      if (disconnectTimerRef.current) {
        clearInterval(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [isDisconnected, userId, onRemoveDisconnected]);

  useEffect(() => {
    if (!stream || !userId || !containerRef.current || isDisconnected) return;

    let mounted = true;
    let pollInterval = null;

    const checkAndApplyAspectRatio = () => {
      const container = containerRef.current;
      if (!container) return false;

      const videoPlayerContainer = container.querySelector('video-player-container');
      if (!videoPlayerContainer) return false;

      let actualVideo = videoPlayerContainer.querySelector('video');
      if (!actualVideo) {
        const videoPlayer = videoPlayerContainer.querySelector('video-player');
        if (videoPlayer && videoPlayer.shadowRoot) {
          actualVideo = videoPlayer.shadowRoot.querySelector('video');
        }
      }
      
      if (actualVideo && actualVideo.tagName === 'VIDEO') {
        const width = actualVideo.videoWidth;
        const height = actualVideo.videoHeight;
        
        if (width && height) {
          const aspectRatio = width / height;
          console.log(`User ${userId} video dimensions: ${width}x${height}, aspect ratio: ${aspectRatio.toFixed(2)}`);
          
          const isMobileVideo = aspectRatio < 1;
          const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

          actualVideo.style.objectFit = isMobileVideo || isMobile ? 'contain' : 'cover';
          
          // Manage resize listener
          if (attachedVideoRef.current !== actualVideo) {
            if (attachedVideoRef.current) {
              attachedVideoRef.current.removeEventListener('resize', checkAndApplyAspectRatio);
            }
            actualVideo.addEventListener('resize', checkAndApplyAspectRatio);
            attachedVideoRef.current = actualVideo;
            console.log('Attached resize listener to video element');
          }

          return true;
        }
      }
      return false;
    };

    const attachVideo = async () => {
      if (!mounted) return;
      try {
        const videoPlayerContainer = containerRef.current.querySelector('video-player-container');
        if (!videoPlayerContainer) {
          console.warn('Static <video-player-container> missing in DOM');
          return;
        }

        if (isVideoOn) {
          // Clear before attaching
          videoPlayerContainer.innerHTML = '';

          // Small delay to mitigate rendering issues in Edge
          await new Promise(resolve => setTimeout(resolve, 0));

          const videoElement = await stream.attachVideo(userId, 3);
          videoElement.style.width = '100%';
          videoElement.style.height = '100%';
          videoElement.style.position = 'absolute';
          videoElement.style.top = '0';
          videoElement.style.left = '0';
          videoElement.className = 'zoom-video-element';
          
          console.log('Video element attached for user:', userId);
          videoPlayerContainer.appendChild(videoElement);
          
          // Wait a bit for element to be fully mounted/loaded
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (!checkAndApplyAspectRatio()) {
            pollInterval = setInterval(() => {
              if (checkAndApplyAspectRatio()) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
            }, 500);
          }
        } else {
          // Detach BEFORE clearing HTML to avoid VideoFrame leaks
          try {
            await stream.detachVideo(userId);
          } catch (e) {
            console.warn('Detach error:', e);
          }
          videoPlayerContainer.innerHTML = '';
          if (attachedVideoRef.current) {
            attachedVideoRef.current.removeEventListener('resize', checkAndApplyAspectRatio);
            attachedVideoRef.current = null;
          }
        }
      } catch (error) {
        console.error(`Error handling video for user ${userId}:`, error);
      }
    };

    attachVideo();

    return () => {
      mounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      if (attachedVideoRef.current) {
        attachedVideoRef.current.removeEventListener('resize', checkAndApplyAspectRatio);
        attachedVideoRef.current = null;
      }
      if (stream && userId) {
        // Crucial: Detach before unmount without clearing innerHTML immediately
        // as the component is being destroyed anyway.
        try {
          stream.detachVideo(userId);
        } catch(e) {
          console.log('Cleanup detach error:', e);
        }
      }
    };
  }, [stream, userId, isVideoOn, isDisconnected]);

  useEffect(() => {
    if (isDisconnected && containerRef.current) {
      const videoPlayerContainer = containerRef.current.querySelector('video-player-container');
      if (stream && userId) {
        try {
          stream.detachVideo(userId);
        } catch (e) {
          console.log('Detach error on disconnect:', e);
        }
      }
      if (videoPlayerContainer) videoPlayerContainer.innerHTML = '';
      if (attachedVideoRef.current) {
        attachedVideoRef.current = null; // Listener will be lost anyway as DOM is cleared
      }
    }
  }, [isDisconnected, stream, userId]);

  
  if (isDisconnected) {
    return (
      <div className={`remote-video disconnected ${className} ${isPip ? 'pip-mode' : ''}`}>
        <div
          ref={containerRef}
          className="video-container disconnected-bg"
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: '#2d2d2d',
          }}
        >
          <video-player-container style={{ width: '100%', height: '100%', position: 'relative' }}></video-player-container>
        </div>
        
        <div className="disconnected-overlay">
          <div className="disconnected-content">
            <div className="spinner-container">
              <Loader2Icon className="reconnection-spinner" />
            </div>
            <p className="disconnected-text">Connection Lost</p>
            <p className="reconnecting-text">Reconnecting... {timeRemaining}s</p>
          </div>
        </div>
        
        <div className="bottom-overlay disconnected-overlay-info">
          <div className="overlay-content">
            <div className="user-details">
              <div className="video-status">
                <div className="status-dot disconnected"></div>
              </div>
              <span className="user-name">{userName || 'User'}</span>
              <span className="disconnect-timer">{timeRemaining}s</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`remote-video ${className} ${isPip ? 'pip-mode ' : ''}`}>
      <div
        ref={containerRef}
        className="video-container"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: '#1a1a1a',overflow: 'hidden',
        }}
      >
        {/* Static Zoom SDK container element */}
        <video-player-container style={{ width: '100%', height: '100%', position: 'relative' }}></video-player-container>
      </div>

      {!isVideoOn && (
        <div className="video-off-placeholder">
          <div className="user-avatar">
            <div className="avatar-circle">{(userName || 'User').charAt(0).toUpperCase()}</div>
          </div>
        </div>
      )}

      <div className={`bottom-overlay ${isPip ? 'pip-bottom-overlay ' : ''}`}>
        <div className="overlay-content">
          <div className="user-details">
            <span className="user-name">{userName || 'User'}</span>
            <div className="video-status">
              {isVideoOn ? <CameraIcon className="camera" /> : <CameraOffIcon className="camera" />}
              {isAudioOn ? <MdMic className="camera" /> : <MdMicOff className="camera" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteVideo;